import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BoHThermometerAnimation, getInitialTimeline } from '../../helpers/animations';
import { getCurrentDateInMillis, getSpecificDateInMillis } from '../../../helpers/utils';
import { getSalesGoalsConditions, getSalesGoalsData } from '../../helpers/boh_spotlights';
import { CongratulationsRunner } from '../CongratulationsRunner';
import Thermometer from './Thermometer/Thermometer';
import GoalUnreachedMessage from '../GoalUnreachedMessage';
import BottomBar from '../../../components/BottomBar';
import SpotlightHeader from '../../Common/Header';
import './ThermometerSalesGoal.scss';
const ThermometerSalesGoal = props => {
  const {
    metadata,
    text,
    data,
    duration,
    playing,
    bohStyle
  } = props;
  const {
    group_name,
    timezone
  } = data;
  const {
    boh_goals: boh_goals_by_dollars,
    boh_goals_by_units,
    boh_goals_by_tickets,
    boh_show_dollar_amount,
    boh_show_tickets,
    boh_event_type,
    boh_date_range,
    boh_display_data
  } = metadata;
  const byUnits = boh_show_dollar_amount === false && boh_show_tickets !== true && boh_display_data === 'plu';
  const byTickets = boh_show_dollar_amount === false && boh_show_tickets === true && boh_display_data === 'plu';
  const boh_goals = byTickets ? boh_goals_by_tickets : byUnits ? boh_goals_by_units : boh_goals_by_dollars;
  const hide_poweredbyshift = metadata.hide_poweredbyshift;
  const {
    accent_color,
    title_color,
    body_color,
    text_accent_color,
    containerStyle
  } = bohStyle;

  // TODO: Remove this useMemo. Currently needed to prevent GSAP breaking
  const salesData = useMemo(() => getSalesGoalsData(boh_goals, data), [boh_goals, data]);
  const [tl, setTl] = useState(getInitialTimeline());
  const [readyToAnimate, setReadyToAnimate] = useState(false);
  const {
    goal,
    stretch_goal,
    sales,
    units,
    tickets
  } = salesData;
  const measureValue = byTickets ? tickets : byUnits ? units : sales;
  const {
    endDate,
    endTime
  } = boh_date_range !== null && boh_date_range !== void 0 ? boh_date_range : {};
  const isDateRangeFinished = boh_event_type === 'custom' && getCurrentDateInMillis(timezone) > getSpecificDateInMillis(endDate, endTime, timezone);

  // TODO: Remove this useMemo. Currently needed to prevent GSAP breaking
  const salesGoalsConditions = useMemo(() => getSalesGoalsConditions({
    ...salesData,
    sales: measureValue
  }, isDateRangeFinished), [salesData, isDateRangeFinished, measureValue]);
  const {
    showStretchGoal,
    showCongratulations,
    showGoalUnreached,
    showTotalSales
  } = salesGoalsConditions;
  const goalThermometerNode = useRef();
  const stretchThermometerNode = useRef();
  const totalSalesNode = useRef();
  const goalUnreachedMessageNode = useRef();
  useEffect(() => {
    if (readyToAnimate) {
      setTl(BoHThermometerAnimation({
        ...salesData,
        sales: measureValue
      }, salesGoalsConditions, {
        goalThermometerNode,
        stretchThermometerNode,
        totalSalesNode,
        goalUnreachedMessageNode
      }));
    }
  }, [readyToAnimate, showCongratulations, playing, salesData, salesGoalsConditions, measureValue]);
  useEffect(() => {
    setReadyToAnimate(tl && !!goalThermometerNode && (showStretchGoal ? !!stretchThermometerNode : true) && (showTotalSales ? !!totalSalesNode : true) && (showGoalUnreached ? !!goalUnreachedMessageNode : true));
  }, [tl, goalThermometerNode, stretchThermometerNode, goalUnreachedMessageNode, totalSalesNode, showGoalUnreached, showStretchGoal, showTotalSales, playing]);
  useEffect(() => {
    if (playing) tl.play();else tl.restart().pause();
  }, [playing, tl]);
  const Header = /*#__PURE__*/React.createElement(SpotlightHeader, {
    text: text,
    metadata: metadata,
    group_name: group_name,
    showUpdatedTime: true
  });
  const stretchThermometer = /*#__PURE__*/React.createElement(Thermometer, {
    shouldAnimate: true,
    ref: stretchThermometerNode,
    className: "stretch-thermometer",
    value: measureValue,
    goal: stretch_goal,
    body_color: body_color,
    goal_description: "STRETCH GOAL",
    byUnits: byUnits,
    byTickets: byTickets
  });
  const goalUnreachedMessage = /*#__PURE__*/React.createElement(GoalUnreachedMessage, {
    ref: goalUnreachedMessageNode
  });
  const finalDescription = byTickets || byUnits ? 'TOTAL' : 'TOTAL SALES AMOUNT';
  const finalValue = byTickets ? `${measureValue} TICKETS` : byUnits ? `${measureValue} UNITS` : `$ ${measureValue.toLocaleString()}`;
  return /*#__PURE__*/React.createElement("div", {
    className: "thermometer-sales-goal-container",
    style: containerStyle
  }, /*#__PURE__*/React.createElement(CongratulationsRunner, {
    color: accent_color,
    textColor: title_color,
    firstText: "GOAL REACHED",
    secondText: "CONGRATULATIONS!",
    header: Header,
    timeout: (duration - 5) * 1000,
    showCongratulations: playing && showCongratulations
  }, /*#__PURE__*/React.createElement("div", {
    className: "thermometer-sales-goal"
  }, Header, /*#__PURE__*/React.createElement("div", {
    className: "thermometers-container"
  }, /*#__PURE__*/React.createElement(Thermometer, {
    shouldAnimate: true,
    ref: goalThermometerNode,
    className: "goal-thermometer",
    value: measureValue,
    goal: goal,
    body_color: body_color,
    goal_description: "GOAL",
    byUnits: byUnits,
    byTickets: byTickets
  }), showGoalUnreached && goalUnreachedMessage, showStretchGoal && stretchThermometer), showTotalSales && /*#__PURE__*/React.createElement("div", {
    ref: totalSalesNode,
    style: {
      color: body_color,
      opacity: 0
    },
    className: "total-sales-container"
  }, /*#__PURE__*/React.createElement("span", {
    className: "total-sales-text"
  }, finalDescription), /*#__PURE__*/React.createElement("span", {
    className: "total-sales-amount"
  }, finalValue))), /*#__PURE__*/React.createElement(BottomBar, {
    accent_color: accent_color,
    accent_text_color: text_accent_color,
    hide_poweredbyshift: hide_poweredbyshift
  })));
};
export default ThermometerSalesGoal;